import type { Emitter } from "@core/events/emitter";
import type { ProjectInfo } from "@models/data/projectInfo";
import type { SharedLinkDto } from "@models/data/sharedLinkDto";
import type { SharedUserRole } from "@models/data/userRolePermissions";

/**
 * `ISharedUsersModel` — web port of Unity's `Models/SharedUsersModel.cs`
 * (`SharedUsersBaseModel` + the sharing legs of `CustomerModel` and
 * `DeepLinkGenerator` that the desktop `ShareViewModel` drives): the headless
 * owner of project sharing.
 *
 * ONE SHAPE DIFFERENCE FROM UNITY, deliberate: Unity's model is a
 * per-open-project `MonoBehaviour` and the projects-grid entry point smuggles
 * a different project in through `ShareViewArgs`. Here every method takes the
 * project EXPLICITLY, so the editor's share dialog (open project) and the
 * grid's (a card, nothing loaded) call the same code with no mode flag —
 * the same reasoning `useSharePanel(target?)` records.
 *
 * Everything here is headless and harness-testable: no Vue, no DOM. The
 * ViewModel owns reactive input state (the email field, the chosen role, the
 * message line) and confirmations; this model owns the RULES and the writes.
 */

/** One roster row — port of `SharedUserJoinedInfo` (`SharedUsersModel.cs`). */
export interface SharedUserJoinedInfo {
  /**
   * `owner` is the synthetic first row; `self` is the current user when they
   * hold access through the ACCOUNT rather than a link (Unity adds it so a
   * same-account member sees themselves); `share` is a real link row.
   */
  readonly kind: "owner" | "self" | "share";
  /** The link row behind a `share` row; `null` for the two synthetic rows. */
  readonly link: SharedLinkDto | null;
  readonly sharedLinkId: string | null;
  /** Lowercased. Empty when unknown (an owner whose email was never resolved). */
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly userId: string | null;
  /** The sharee's natural account, which owns their profile picture. */
  readonly accountId: string | null;
  readonly planType: number;
  readonly accountRole: number;
  /**
   * The role to DISPLAY: Unity `CreateJoinedInfo` clamps the link's role by
   * the sharee's account role (Reader → Viewer, Commenter caps Editor) and
   * shows the current user's LIVE project role for their own row.
   */
  readonly role: SharedUserRole;
  readonly isSelf: boolean;
  /** `Visited && userId == null` — the row reads "Deleted User" upstream. */
  readonly isDeletedUser: boolean;
  /** Unity `CanRevoke` = `CanEditUsers() || isSelf` (owner row: never). */
  readonly canRevoke: boolean;
  /** Unity `CanChangeRole` = `CanEditUsers() && !isDeleted` (owner/self: never). */
  readonly canChangeRole: boolean;
  /**
   * Unity `SharedUsersListItemViewModel.AvailableActions`' role subset,
   * before its final Revoke/Leave command: Reader → Viewer only; Commenter →
   * Viewer+Commenter; Writer adds Editor unless enterprise→IndividualFree.
   */
  readonly availableRoles: ReadonlyArray<SharedUserRole>;
  /** The link's `Visited` flag, or true when the user is live in the session. */
  readonly visited: boolean;
}

/** Outcome of one `inviteAsync` — `message` is what Unity shows under the row. */
export interface InviteResult {
  readonly invited: ReadonlyArray<string>;
  readonly failed: ReadonlyArray<string>;
  readonly message: string | null;
}

export interface ISharedUsersModel {
  /**
   * Fired after every LOCAL sharing mutation this model performs (invite,
   * revoke, role change, account rule). Roster consumers reload on it; the
   * participants list refreshes on it (`ParticipantsListViewModel.
   * OnSharingChanged`). Peer-originated changes arrive through
   * `change_sharing` on the permissions model instead.
   */
  readonly onSharingChanged: Emitter<{ projectId: string }>;

  /**
   * Bridge for a peer's inbound `change_sharing`: announce the project so an
   * open share sheet and participant roster re-read their current models.
   * Local writers emit this themselves and ignore their own wire echo.
   */
  notifySharingChanged(projectId: string): void;

  // -- Gating (`SharedUsersBaseModel` predicates) --------------------------

  /** `CanInviteUsers()` — the active role `CanShareProject()`: Owner | Editor. */
  canInviteUsers(project: ProjectInfo): boolean;
  /** `CanEditUsers()` — identical rule to `canInviteUsers` upstream. */
  canEditUsers(project: ProjectInfo): boolean;
  /**
   * `CanShareWithAccount()` — not on external (hybrid-cloud) credentials,
   * `CanEditUsers()`, and the account plan is EnterpriseFree | EnterprisePaid.
   */
  canShareWithAccount(project: ProjectInfo): boolean;
  /**
   * `CanInviteExternalUsers()` — false only for an SSO user on a hybrid-cloud
   * bucket that is not Campfire's. The web models neither, so this is true.
   */
  canInviteExternalUsers(): boolean;
  /**
   * `ShareViewModel.UpdateShareRuleText` — the rule line under the invite
   * row: enterprise accounts get the "add them as guests" note, others "".
   */
  shareRuleMessage(project: ProjectInfo): string;

  // -- Reads -----------------------------------------------------------

  /** Account that owns the project's rows (`ProjectInfo.customerId`, else yours). */
  projectCustomerId(project: ProjectInfo): string;
  /**
   * `GetSharedLinksByProject` — EVERY link row of the project, unfiltered:
   * the roster rows, the `"internal"` Copy Link row and any `NoAccess` rows.
   * Callers that want Unity's roster set (`GetProjectShares`) pass the list
   * through {@link getSharedUsersAsync}, which applies that filter; Copy Link
   * and the invite's duplicate check need the raw list.
   */
  getProjectLinksAsync(project: ProjectInfo, signal?: AbortSignal): Promise<ReadonlyArray<SharedLinkDto>>;
  /**
   * `LoadSharedUsers()` — the full roster: one row per ROSTER link (identity
   * resolved through `lookup`, cached per email), the owner row first, and a
   * `self` row when the current user is neither owner nor a sharee.
   */
  getSharedUsersAsync(
    project: ProjectInfo,
    links: ReadonlyArray<SharedLinkDto>,
    signal?: AbortSignal,
  ): Promise<ReadonlyArray<SharedUserJoinedInfo>>;
  /**
   * Unity's `DeepLinkGenerator.URIForLink` query contract on the current WEB
   * deployment (`/query?sl&act&prj`), including a named channel base.
   */
  projectLinkUrl(project: ProjectInfo, link: SharedLinkDto): string;

  // -- Writes ----------------------------------------------------------

  /**
   * `ShareViewModel.InviteUser()` — comma-separated emails, each validated
   * (`CheckMailValidity`), looked up, run through the guest / account /
   * safelist guards, written as a `SceneLink` row with the deterministic id,
   * and emailed. Returns Unity's message for the message line.
   */
  inviteAsync(
    project: ProjectInfo,
    emailInput: string,
    role: SharedUserRole,
    existingLinks: ReadonlyArray<SharedLinkDto>,
  ): Promise<InviteResult>;
  /**
   * `CreateInternalProjectLink` — the `"internal"` ProjectLink/Viewer row
   * behind Copy Link. Returns the existing row when there already is one
   * (same deterministic id), else writes and returns the new one. `null`
   * when storage is unavailable.
   */
  createInternalProjectLinkAsync(
    project: ProjectInfo,
    existingLinks: ReadonlyArray<SharedLinkDto>,
  ): Promise<SharedLinkDto | null>;
  /**
   * `SharedUsersBaseModel.AccountRole` setter → `UpdateCustomerProjectAccountRole`,
   * then `SendAccountRoleUpdate` (`change_sharing AccountRoleChanged`).
   */
  setAccountRoleAsync(project: ProjectInfo, accountRole: SharedUserRole): Promise<void>;
  /**
   * `ShareViewModel.UpdateSharedLink` → `UpdateSharedLinkUserRole` + timestamp
   * bump + `change_sharing UserRoleChanged` to the row's account.
   */
  updateSharedLinkUserRoleAsync(project: ProjectInfo, link: SharedLinkDto, role: SharedUserRole): Promise<void>;
  /**
   * `ShareViewModel.RevokeSharedLink`'s post-confirmation branch: an
   * enterprise member leaving takes `RemoveSelfFromAccountSharing` (a
   * `NoAccess` link for themselves + `evict_user` at themselves); anyone else
   * deletes the row (`OnConfirmRevokeSharedLink`) with its `evict_user` /
   * `ProjectUnshared` legs. The CONFIRMATION is the ViewModel's.
   */
  revokeAccessAsync(project: ProjectInfo, row: SharedUserJoinedInfo): Promise<void>;
  /** The link-deleting half of {@link revokeAccessAsync}, for callers holding a row. */
  revokeSharedLinkAsync(project: ProjectInfo, link: SharedLinkDto): Promise<void>;
  /** `RevokeSharedLink`'s `isSelf && IsEnterpriseAccount` test, for the roster's self row. */
  canRemoveSelfFromAccountSharing(project: ProjectInfo): boolean;
  /**
   * `CustomerModel.DeleteProjectAsync`'s sharing leg: a `ProjectUnshared` to
   * every external sharee's account so the project leaves their grid. Called
   * by the project list BEFORE it purges the rows.
   */
  notifyProjectDeletedAsync(project: ProjectInfo, links: ReadonlyArray<SharedLinkDto>): Promise<void>;
  /**
   * `CustomerModel.MarkProjectAsVisited` — flip the current user's link to
   * `Visited` on first open. Runs automatically on active-project change;
   * exposed for the deep-link handler.
   */
  markProjectAsVisitedAsync(project: ProjectInfo): Promise<void>;
}

export const SHARED_USERS_MODEL = Symbol.for("Campfire.SharedUsersModel");
